/**
 * 계약서 (운영자 › 콘텐츠).
 *
 * 빈칸(갑 정보·시작일·월 요금·목표 질문)을 채워 저장하고, 오른쪽 미리보기를 그대로 인쇄·PDF 로 뽑아
 * 서명을 받는다. 계약 문구는 `contract/template.ts` 한 곳에 있다 — 화면은 빈칸만 다룬다.
 * ★ 미리보기는 iframe 이다 — 계약서의 @page·body 스타일이 어드민 화면에 새지 않고, 인쇄도 그 문서만 나간다.
 */
import { CONTRACT_MAX_QUERIES, type Contract, type ContractStatus } from '@geo-admin/shared/contract/api/contract.ts';
import { contract } from '@geo-admin/shared/contract/api/index.ts';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { type ContractFields, contractFileTitle, koreanDate, renderContract } from '../contract/render.ts';
import { DataTable } from '../components/data-table.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandKindTabs, BrandOptions, useBrandChoices, useBrandKind } from '../components/BrandKindTabs.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label, Select, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

const STATUS_LABEL: Record<ContractStatus, string> = { draft: '작성 중', signed: '체결' };

/** 오늘(사용자 PC 기준) — 계약서 날짜 칸의 기본값일 뿐이다 */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ContractsPage() {
  // 「실제 | 테스트」 탭 — 고객 없는 계약서는 실제 탭에 있다(261023-test-brand)
  const brandKind = useBrandKind();
  const list = useApi(contract.listContracts, { query: { brandKind } });
  const columns: ColumnDef<Contract, unknown>[] = [
    {
      header: '업체',
      accessorKey: 'companyName',
      cell: ({ row }) => (
        <Link to="/admin/contracts/$id" params={{ id: row.original.id }} className="font-medium hover:underline">
          {row.original.companyName}
        </Link>
      ),
    },
    { header: '시작일', accessorKey: 'startDate', cell: ({ row }) => koreanDate(row.original.startDate) },
    {
      header: '월 요금',
      accessorKey: 'monthlyFee',
      cell: ({ row }) => `${row.original.monthlyFee.toLocaleString('ko-KR')}원`,
    },
    { header: '질문', id: 'queries', cell: ({ row }) => `${row.original.queries.length}개` },
    {
      header: '상태',
      accessorKey: 'status',
      cell: ({ row }) => (
        <Badge tone={row.original.status === 'signed' ? 'green' : 'slate'}>{STATUS_LABEL[row.original.status]}</Badge>
      ),
    },
    { header: '작성', accessorKey: 'createdAt', cell: ({ row }) => dateTime(row.original.createdAt) },
  ];

  return (
    <>
      <PageTitle
        title="계약서"
        description="서비스 이용 계약서의 빈칸을 채워 저장하고, 인쇄·PDF 로 뽑아 서명을 받습니다"
        actions={
          <Link to="/admin/contracts/new">
            <Button>새 계약서</Button>
          </Link>
        }
      />
      <BrandKindTabs />
      <Card>
        <CardBody>
          {list.error ? (
            <ErrorBox error={list.error} />
          ) : !list.data ? (
            <Loading />
          ) : (
            <DataTable columns={columns} data={list.data.contracts} empty="아직 작성한 계약서가 없습니다" />
          )}
        </CardBody>
      </Card>
    </>
  );
}

export function ContractNewPage() {
  return <ContractEditor current={null} />;
}

export function ContractDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const got = useApi(contract.getContract, { params: { id } });
  if (got.error) return <ErrorBox error={got.error} />;
  if (!got.data) return <Loading />;
  return <ContractEditor key={got.data.contract.id} current={got.data.contract} />;
}

function ContractEditor({ current }: { current: Contract | null }) {
  const navigate = useNavigate();
  const brands = useBrandChoices();
  const create = useApiMutation(contract.createContract);
  const update = useApiMutation(contract.updateContract);
  const del = useApiMutation(contract.deleteContract);
  const frame = useRef<HTMLIFrameElement>(null);

  const [brandId, setBrandId] = useState(current?.brandId ?? '');
  const [f, setF] = useState<ContractFields>(() => ({
    companyName: current?.companyName ?? '',
    representativeName: current?.representativeName ?? '',
    businessNumber: current?.businessNumber ?? '',
    address: current?.address ?? '',
    contact: current?.contact ?? '',
    startDate: current?.startDate ?? today(),
    contractDate: current?.contractDate ?? today(),
    monthlyFee: current?.monthlyFee ?? 0,
    extraQueryFee: current?.extraQueryFee ?? 0,
    queries: current?.queries ?? [],
  }));
  const [queriesText, setQueriesText] = useState((current?.queries ?? []).join('\n'));
  const [status, setStatus] = useState<ContractStatus>(current?.status ?? 'draft');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof ContractFields>(k: K, v: ContractFields[K]) => setF((prev) => ({ ...prev, [k]: v }));

  // 고객을 바꿨을 때만 채운다 — 저장된 계약서를 열 때(brandId 초기값)는 덮지 않는다
  const [picked, setPicked] = useState(false);
  const source = useApi(contract.contractSource, { query: { brandId } }, { enabled: picked && brandId !== '' });
  useEffect(() => {
    if (!picked || !source.data) return;
    const s = source.data;
    setF((prev) => ({
      ...prev,
      companyName: s.companyName,
      representativeName: s.representativeName ?? prev.representativeName,
      contact: s.contact ?? prev.contact,
      queries: s.queries,
    }));
    setQueriesText(s.queries.join('\n'));
  }, [picked, source.data]);

  const onQueries = (v: string) => {
    setQueriesText(v);
    set(
      'queries',
      v
        .split('\n')
        .map((q) => q.trim())
        .filter(Boolean),
    );
  };

  const html = useMemo(() => renderContract(f), [f]);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    setNotice(null);
    const body = { ...f, brandId: brandId === '' ? null : brandId };
    try {
      if (current) {
        await update.mutateAsync({ params: { id: current.id }, body: { ...body, status } });
        setNotice('저장했습니다');
      } else {
        const r = await create.mutateAsync({ body });
        void navigate({ to: '/admin/contracts/$id', params: { id: r.contract.id } });
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const onDelete = async () => {
    if (!current || !confirm('이 계약서를 삭제합니다.')) return;
    await del.mutateAsync({ params: { id: current.id } });
    void navigate({ to: '/admin/contracts' });
  };

  // 브라우저마다 PDF 기본 파일명을 iframe 문서 제목이나 바깥 화면 제목에서 가져온다 — 둘 다 맞춘다.
  // print() 는 인쇄 창이 닫힐 때까지 돌아오지 않으므로 바로 뒤에서 원래 제목으로 되돌린다
  const onPrint = () => {
    const prev = document.title;
    document.title = contractFileTitle(f.companyName);
    frame.current?.contentWindow?.print();
    document.title = prev;
  };

  const pending = create.isPending || update.isPending;
  const tooMany = f.queries.length > CONTRACT_MAX_QUERIES;

  return (
    <>
      <PageTitle
        title={current ? `계약서 — ${current.companyName}` : '새 계약서'}
        description={
          current
            ? `${STATUS_LABEL[current.status]} · ${dateTime(current.createdAt)} 작성`
            : '빈칸을 채우면 오른쪽 계약서에 바로 반영됩니다'
        }
        actions={
          <Link to="/admin/contracts" className="text-sm text-slate-500 hover:text-slate-900">
            ← 계약서 목록
          </Link>
        }
      />
      <div className="grid gap-4 xl:grid-cols-[400px_1fr]">
        <Card className="self-start">
          <CardHeader title="빈칸 채우기" />
          <CardBody>
            <form className="grid gap-3" onSubmit={save}>
              <Label>
                고객 (고르면 업체명·대표자·연락처·목표 질문을 채웁니다)
                <Select
                  value={brandId}
                  onChange={(e) => {
                    setBrandId(e.target.value);
                    setPicked(true);
                  }}
                >
                  <option value="">직접 입력 (등록 안 된 업체)</option>
                  <BrandOptions real={brands.real} test={brands.test} />
                </Select>
              </Label>
              <Label>
                상호
                <Input
                  value={f.companyName}
                  onChange={(e) => set('companyName', e.target.value)}
                  required
                  maxLength={120}
                />
              </Label>
              <Label>
                대표자
                <Input
                  value={f.representativeName}
                  onChange={(e) => set('representativeName', e.target.value)}
                  required
                  maxLength={60}
                />
              </Label>
              <Label>
                사업자등록번호 (비우면 손으로 씁니다)
                <Input
                  value={f.businessNumber}
                  onChange={(e) => set('businessNumber', e.target.value)}
                  maxLength={20}
                />
              </Label>
              <Label>
                주소
                <Input value={f.address} onChange={(e) => set('address', e.target.value)} maxLength={200} />
              </Label>
              <Label>
                연락처
                <Input value={f.contact} onChange={(e) => set('contact', e.target.value)} maxLength={120} />
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Label>
                  시작일 (첫 결제일)
                  <Input type="date" value={f.startDate} onChange={(e) => set('startDate', e.target.value)} required />
                </Label>
                <Label>
                  계약일 (서명란)
                  <Input
                    type="date"
                    value={f.contractDate}
                    onChange={(e) => set('contractDate', e.target.value)}
                    required
                  />
                </Label>
              </div>
              <Label>
                월 구독 요금 (원, 부가세 별도)
                <Input
                  type="number"
                  min={0}
                  step={10000}
                  value={f.monthlyFee === 0 ? '' : f.monthlyFee}
                  onChange={(e) => set('monthlyFee', Number(e.target.value || 0))}
                  required
                />
              </Label>
              <Label>
                추가 질문 1개당 월 요금 (원, 부가세 별도 · 비우면 계약서에 빈칸)
                <Input
                  type="number"
                  min={0}
                  step={10000}
                  value={f.extraQueryFee === 0 ? '' : f.extraQueryFee}
                  onChange={(e) => set('extraQueryFee', Number(e.target.value || 0))}
                />
              </Label>
              <Label>
                목표 질문 (한 줄에 하나 · {f.queries.length}개)
                <Textarea value={queriesText} onChange={(e) => onQueries(e.target.value)} rows={5} required />
                {tooMany ? <FieldError>목표 질문은 {CONTRACT_MAX_QUERIES}개까지입니다</FieldError> : null}
              </Label>
              {current ? (
                <Label>
                  상태
                  <Select value={status} onChange={(e) => setStatus(e.target.value as ContractStatus)}>
                    <option value="draft">작성 중</option>
                    <option value="signed">체결 (서명 받음)</option>
                  </Select>
                </Label>
              ) : null}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button type="submit" disabled={pending || tooMany || f.queries.length === 0}>
                  {pending ? '저장 중…' : '저장'}
                </Button>
                <Button variant="outline" onClick={onPrint}>
                  인쇄 · PDF 저장
                </Button>
                {current ? (
                  <Button variant="ghost" onClick={() => void onDelete()}>
                    삭제
                  </Button>
                ) : null}
              </div>
              {notice ? <p className="text-sm text-emerald-700">{notice}</p> : null}
              <FieldError>{error}</FieldError>
            </form>
          </CardBody>
        </Card>
        <Card className="overflow-hidden">
          <CardHeader title="계약서 미리보기" description="인쇄하면 이 문서만 A4 로 나갑니다" />
          <iframe ref={frame} title="계약서 미리보기" srcDoc={html} className="h-[1100px] w-full bg-slate-200" />
        </Card>
      </div>
    </>
  );
}
